import React, {memo} from 'react';
import cx from 'classnames';
import {priority} from '@priority-software/priority-proto-api/lib';
import {Flex} from '@priority-software/priority-style-react';
import {ActionButton} from '../../../ActionButton';
import {ActionsDropdown} from '../../../ActionsDropdown';
import {ActionIcon} from '../../../../../../components/ActionIcon';
import {getEntityIcon} from '../../../../../../components/ActionIcon/actionIconData';
import {createDataTestId} from '../../../../../../utils/dataTestId';
import {formsStore} from '../../../../../../stores/Form';
import {showConfirmMessage} from '../../../../../../_shared/Messages/actions/MessageActions';
import {defaultConfirmProps} from '../../../../actions/messages';
import {startDirectActivation} from '../../../../actions/activations';
import {texts} from '../../../AdvancedSearch/AdvancedSearchDrawer/texts';
import styles from './actions.module.scss';
import {ModernTooltip} from '../../../../../../components/Common/Tooltip/ModernTooltip';

type Props = {
    actions: priority.netitems.form.IAction[];
    disabled?: boolean;
};

const PRIMARY_ACTIONS_COUNT = 2;

export const Actions = memo((props: Props) => {
    const {actions, disabled} = props;

    const primaryActions = actions?.slice(0, PRIMARY_ACTIONS_COUNT) || [];
    const listActions = actions?.slice(PRIMARY_ACTIONS_COUNT) || [];

    const runAction = (action: priority.netitems.form.IAction) => {
        if (disabled) return;

        if (action.Confirm) {
            showConfirmMessage({
                ...defaultConfirmProps(),
                title: action.Title,
                onConfirm: () => continueAction(action)
            });
        } else {
            continueAction(action);
        }
    };

    const continueAction = async (action: priority.netitems.form.IAction) => {
        const form = formsStore.getActiveForm();
        startDirectActivation(form, action);
    };

    return (
        <>
            <Flex componentClasses={styles.component} inline>
                {primaryActions.map(action => (
                    <ActionButton
                        key={action?.Ename}
                        text={action?.Title}
                        onClick={() => runAction(action)}
                        componentClasses={cx(
                            styles.btn,
                            getEntityIcon(action?.Type as number)?.hoverClass
                        )}
                        disabled={disabled}
                        {...createDataTestId(`action-tag-${action?.Ename}`, true)}
                    >
                        <ActionIcon
                            disabled={disabled}
                            type={action?.Type as number}
                            componentClasses={styles.iconWrapper}
                        />
                    </ActionButton>
                ))}
            </Flex>

            {listActions.length > 0 && (
                <ModernTooltip tooltip={texts.actionsTooltip} position="bottom">
                    <ActionsDropdown
                        actions={listActions}
                        disabled={disabled}
                        runAction={runAction}
                        openingSide={
                            formsStore.currentScreen.isRecordDialog ? 'leftSide' : 'rightSide'
                        }
                    />
                </ModernTooltip>
            )}
        </>
    );
});
Actions.displayName = 'Actions';
